import { useRef, useCallback, useEffect } from "react"

/** Default auto-save delay in milliseconds. */
const DEFAULT_DELAY = 3_000

interface UseAutoSaveOptions<T> {
  /** The current value to save. */
  value: T
  /** Async save function — called with the latest value. */
  saveFn: (value: T) => void | Promise<void>
  /** Debounce delay in ms (default: 3000). */
  delay?: number
}

interface UseAutoSaveReturn<T> {
  /** Call on every input change to schedule a debounced save. */
  scheduleAutoSave: (newValue: T) => void
  /** Call on blur to save immediately (and cancel pending timer). */
  saveNow: (newValue: T) => void
  /** Whether a save is currently pending (timer running). */
  isPending: boolean
  /** Cancel any pending auto-save without saving. */
  cancel: () => void
}

/**
 * Reusable auto-save hook with debounced writes and immediate-on-blur.
 *
 * Usage:
 * ```tsx
 * const { scheduleAutoSave, saveNow, cancel } = useAutoSave({
 *   value: title,
 *   saveFn: (val) => updateTaskTitle(taskId, val),
 *   delay: 3000,
 * })
 * ```
 */
export function useAutoSave<T>({
  saveFn,
  delay = DEFAULT_DELAY,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef(false)
  const saveFnRef = useRef(saveFn)

  // Keep saveFn ref up to date without re-creating callbacks
  saveFnRef.current = saveFn

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pendingRef.current = false
  }, [])

  const scheduleAutoSave = useCallback(
    (newValue: T) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      pendingRef.current = true
      timerRef.current = setTimeout(() => {
        saveFnRef.current(newValue)
        pendingRef.current = false
        timerRef.current = null
      }, delay)
    },
    [delay],
  )

  const saveNow = useCallback(
    (newValue: T) => {
      cancel()
      saveFnRef.current(newValue)
    },
    [cancel],
  )

  return {
    scheduleAutoSave,
    saveNow,
    isPending: pendingRef.current,
    cancel,
  }
}
